import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatPageService } from '../services/index.js';
import logo from '../assets/Advico logo.png';
import { LogIn, Moon, Sun, Bot, User } from 'lucide-react';
import Sidebar from '../components/common/Sidebar.jsx';
import { ChatArea } from '../components/userDashboard/ui/index.js';
import { useDashboard, useVoiceRecording } from '../components/userDashboard/hooks/index.js';

const isLoggedIn = () => !!(
  localStorage.getItem('accessToken') &&
  localStorage.getItem('userId') &&
  new Date(localStorage.getItem('expiresAtUtc')) > new Date()
);

/* ─── Guest read-only view ─── */
function GuestSharedChat({ messages, title, loading, error, darkMode, setDarkMode, navigate }) {
  const messagesEndRef = useRef(null);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  return (
    <div className={`flex h-screen w-full overflow-hidden ${darkMode ? 'dark bg-slate-900 text-slate-100' : 'bg-[#e8edf3] text-slate-900'}`}>
      <aside className="w-64 flex-shrink-0 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col">
        <div className="h-20 flex items-center px-4 border-b border-slate-100 dark:border-slate-700">
          <img src={logo} alt="Logo" className="h-[52px] w-auto max-w-[120px] object-contain" />
        </div>
        <div className="flex-1 flex flex-col p-4 gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">Shared Conversation</p>
            <div className="px-3 py-2 rounded-xl bg-[#e8edf5] dark:bg-[#4a8ce3]/20 text-[#1d57a5] dark:text-[#4a8ce3] text-sm font-medium truncate">{title}</div>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3 bg-slate-50 dark:bg-slate-900/40">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Get started with GenXAI</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Log in to save and revisit previous conversations.</p>
            <button onClick={() => navigate('/login')} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1d57a5] hover:bg-[#174a8c] text-white rounded-xl text-sm font-medium transition-colors">
              <LogIn size={16} /> Log In
            </button>
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex justify-end">
          <button onClick={() => setDarkMode(d => !d)} className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 ${darkMode ? 'bg-[#1d57a5]' : 'bg-slate-300'}`}>
            <span className={`absolute left-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-300 ${darkMode ? 'translate-x-5' : 'translate-x-0'}`}>
              {darkMode ? <Moon size={14} className="text-[#1d57a5]" /> : <Sun size={14} className="text-amber-500" />}
            </span>
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="px-8 pt-5 pb-3 border-b border-slate-200/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm">
          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">{title}</h1>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Read-only shared conversation · <button onClick={() => navigate('/login')} className="text-[#1d57a5] dark:text-[#4a8ce3] hover:underline">Log in</button> to chat</p>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 bg-[#e8edf3] dark:bg-slate-900">
          {loading && <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-4 border-[#1d57a5] border-t-transparent rounded-full animate-spin" /></div>}
          {error && <div className="flex items-center justify-center h-full"><p className="text-red-500">{error}</p></div>}
          {!loading && !error && (
            <div className="max-w-4xl mx-auto space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex w-full ${msg.senderType === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.senderType === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-[#1d57a5] flex items-center justify-center flex-shrink-0 mr-3 mt-0.5"><Bot size={16} className="text-white" /></div>
                  )}
                  <div className={`text-[15px] leading-relaxed whitespace-pre-wrap max-w-[70%] ${msg.senderType === 'user' ? 'p-3 bg-[#c7d9ef] border border-[#9eb9da] dark:bg-slate-700 dark:border-slate-500 text-slate-900 dark:text-white rounded-[1.25rem] rounded-tr-md' : 'text-slate-800 dark:text-slate-200'}`}>
                    {msg.messageText}
                  </div>
                  {msg.senderType === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center flex-shrink-0 ml-3 mt-0.5"><User size={16} className="text-slate-600 dark:text-slate-300" /></div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ─── Authenticated full chat view ─── */
function AuthSharedChat({ sharedMessages, title, darkMode, setDarkMode }) {
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [activeUseCase] = useState({ id: 'loaded', userUseCaseId: null, useCaseName: title });

  const {
    messages, input, setInput, isTyping, copiedId, setCopiedId, tokenUsage,
    selectedModel, setSelectedModel, isModelDropdownOpen, setIsModelDropdownOpen,
    handleSendMessage, handleTryAgain, messagesEndRef, setMessages, parseHistoryMessages,
  } = useDashboard(activeUseCase, 0, null);

  const { isRecording, toggleRecording } = useVoiceRecording(setInput);

  const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
  const displayEmail = storedUser?.email || '';
  const displayName = storedUser?.groupName || displayEmail.split('@')[0] || 'User';

  const fetchHistory = useCallback(() => {
    chatPageService.fetchChatHistory()
      .then(r => { const l = r?.data || r || []; setChatHistory(Array.isArray(l) ? l : []); })
      .catch(() => setChatHistory([]));
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Load shared messages once
  useEffect(() => {
    if (!sharedMessages.length) return;
    const formatted = parseHistoryMessages(
      sharedMessages.map((m, i) => ({ ...m, messageId: i + 1, messageJSON: null }))
    );
    setMessages(formatted);
  }, [sharedMessages, parseHistoryMessages, setMessages]);

  const handleLogout = () => { localStorage.clear(); navigate('/login'); };
  const handlePin = async (id, isPinned) => { await chatPageService.pinChat(id, !isPinned); fetchHistory(); };
  const handleRename = async (id, t) => { await chatPageService.renameChat(id, t); fetchHistory(); };
  const handleDelete = async (id) => { await chatPageService.deleteChat(id); fetchHistory(); };
  const handleArchive = async (id, isArchived = true) => { await chatPageService.archiveChat(id, isArchived); fetchHistory(); };

  const handleEdit = useCallback((msgId, newText) => {
    setMessages(prev => { const idx = prev.findIndex(m => m.messageId === msgId); return idx === -1 ? prev : prev.slice(0, idx); });
    setInput(newText);
  }, [setMessages, setInput]);

  return (
    <div className={`flex h-screen w-full overflow-hidden ${darkMode ? 'dark bg-slate-900 text-slate-100' : 'bg-[#e8edf3] text-slate-900'}`}>
      <Sidebar
        navItems={[]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setActiveUseCase={() => {}}
        activeUseCase={activeUseCase}
        onNavChange={() => {}}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        user={{ username: displayName, email: displayEmail }}
        handleLogout={handleLogout}
        title="User Portal"
        chatHistory={chatHistory}
        isSidebarOpen={true}
        onUseCasesClick={() => navigate('/user')}
        onPin={handlePin}
        onRename={handleRename}
        onDelete={handleDelete}
        onArchive={handleArchive}
        onShare={() => {}}
        onRenamingChange={() => {}}
      />

      <section className="flex-1 flex flex-col h-full overflow-hidden relative transition-all duration-300">
        <ChatArea
          activeUseCase={activeUseCase}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          isModelDropdownOpen={isModelDropdownOpen}
          setIsModelDropdownOpen={setIsModelDropdownOpen}
          messages={messages}
          isTyping={isTyping}
          copiedId={copiedId}
          setCopiedId={setCopiedId}
          handleTryAgain={handleTryAgain}
          handleEdit={handleEdit}
          messagesEndRef={messagesEndRef}
          input={input}
          setInput={setInput}
          isRecording={isRecording}
          toggleRecording={toggleRecording}
          handleSendMessage={async (...args) => { await handleSendMessage(...args); fetchHistory(); }}
          tokenUsage={tokenUsage}
          darkMode={darkMode}
          onShareChat={() => {}}
          onPinChat={() => {}}
          onArchiveChat={() => {}}
          onDeleteChat={() => {}}
          isPinned={false}
        />
      </section>
    </div>
  );
}

/* ─── Root ─── */
export default function SharedChat() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [sharedMessages, setSharedMessages] = useState([]);
  const [title, setTitle] = useState('Shared Chat');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    chatPageService.getSharedChats(shareId)
      .then(res => {
        const data = res?.data || [];
        if (data.length) setTitle(data[0].title || 'Shared Chat');
        setSharedMessages(data);
      })
      .catch(() => setError('Failed to load shared chat.'))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) return (
    <div className={`flex h-screen w-full items-center justify-center ${darkMode ? 'dark bg-slate-900' : 'bg-[#e8edf3]'}`}>
      <div className="w-10 h-10 border-4 border-[#1d57a5] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (isLoggedIn()) {
    return <AuthSharedChat sharedMessages={sharedMessages} title={title} darkMode={darkMode} setDarkMode={setDarkMode} />;
  }

  return <GuestSharedChat messages={sharedMessages} title={title} loading={false} error={error} darkMode={darkMode} setDarkMode={setDarkMode} navigate={navigate} />;
}
